import React from 'react'

interface DonutChartSegment {
  value: number
  color: string
}

interface DonutChartProps {
  segments: DonutChartSegment[]
  centerLabel?: string
  centerSubLabel?: string
  size?: number
  strokeWidth?: number
  className?: string
  centerContent?: React.ReactNode
  segmentGapDegrees?: number
  roundedCaps?: boolean
  startAngle?: number
}

export const DonutChart: React.FC<DonutChartProps> = ({
  segments,
  size = 100,
  strokeWidth = 12,
  className = '',
  centerContent,
  segmentGapDegrees = 0,
  roundedCaps = true,
  startAngle = -90,
}) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const total = segments.reduce((acc, s) => acc + s.value, 0)
  const gapLength = (Math.max(0, segmentGapDegrees) / 360) * circumference
  const totalGapLength = gapLength * segments.length
  const availableLength = Math.max(0, circumference - totalGapLength)
  
  let currentOffset = 0

  return (
    <div className={`relative flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((segment, index) => {
          const percentage = total > 0 ? segment.value / total : 0
          const segmentLength = percentage * availableLength
          const strokeDasharray = `${segmentLength} ${circumference}`
          const strokeDashoffset = -currentOffset
          currentOffset += segmentLength + gapLength

          return (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="transparent"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap={roundedCaps ? 'round' : 'butt'}
              transform={`rotate(${startAngle} ${size / 2} ${size / 2})`}
            />
          )
        })}
      </svg>
      {centerContent && (
        <div className="absolute inset-0 flex items-center justify-center">
          {centerContent}
        </div>
      )}
    </div>
  )
}

export default DonutChart
